import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAuth } from '@/hooks/useAuth'
import { changePassword, getProfile, signOutEverywhere, updateProfile } from '@/services/api/account.service'
import { parseApiError } from '@/utils/error-parser'

function ProfileCard({ fullName, email }: { fullName: string; email: string }) {
  const queryClient = useQueryClient()
  const [name, setName] = useState(fullName)
  const [saving, setSaving] = useState(false)
  return (
    <Card>
      <CardHeader><CardTitle>Profile</CardTitle><CardDescription>{email}</CardDescription></CardHeader>
      <CardContent className="space-y-3">
        <div className="max-w-md space-y-1.5">
          <Label htmlFor="account-name">Full name</Label>
          <Input id="account-name" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />
        </div>
        <Button
          disabled={saving || !name.trim() || name.trim() === fullName}
          onClick={async () => {
            setSaving(true)
            try {
              await updateProfile(name.trim())
              await queryClient.invalidateQueries({ queryKey: ['account-profile'] })
              toast.success('Name updated')
            } catch (e) {
              toast.error(parseApiError(e).detail || 'Could not save')
            } finally {
              setSaving(false)
            }
          }}
        >
          Save
        </Button>
      </CardContent>
    </Card>
  )
}

function PasswordCard({ onChanged }: { onChanged: () => void }) {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [saving, setSaving] = useState(false)
  const mismatch = confirm !== '' && confirm !== next
  return (
    <Card>
      <CardHeader>
        <CardTitle>Password</CardTitle>
        <CardDescription>Changing it signs you out on every device. At least 8 characters.</CardDescription>
      </CardHeader>
      <CardContent className="max-w-md space-y-3">
        <div className="space-y-1.5"><Label htmlFor="pw-current">Current password</Label><Input id="pw-current" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} /></div>
        <div className="space-y-1.5"><Label htmlFor="pw-new">New password</Label><Input id="pw-new" type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} /></div>
        <div className="space-y-1.5">
          <Label htmlFor="pw-confirm">Repeat new password</Label>
          <Input id="pw-confirm" type="password" autoComplete="new-password" aria-invalid={mismatch} value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          {mismatch && <p className="text-sm text-destructive">Passwords do not match.</p>}
        </div>
        <Button
          disabled={saving || !current || next.length < 8 || next !== confirm}
          onClick={async () => {
            setSaving(true)
            try {
              await changePassword(current, next)
              onChanged()
            } catch (e) {
              toast.error(parseApiError(e).detail || 'Could not change the password')
              setSaving(false)
            }
          }}
        >
          Change password
        </Button>
      </CardContent>
    </Card>
  )
}

export function AccountPage() {
  const { logout } = useAuth()
  const { data: profile } = useQuery({ queryKey: ['account-profile'], queryFn: getProfile })
  const [signingOut, setSigningOut] = useState(false)

  const endSession = async (message: string) => {
    toast.success(message)
    await logout()
  }

  return (
    <div className="max-w-3xl space-y-4">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">My account</h1>
        <p className="text-sm text-muted-foreground">Your name, password and signed-in devices.</p>
      </div>
      {profile && <ProfileCard key={profile.fullName ?? ''} fullName={profile.fullName ?? ''} email={profile.email} />}
      <PasswordCard onChanged={() => void endSession('Password changed. Please sign in again.')} />
      <Card>
        <CardHeader><CardTitle>Sessions</CardTitle><CardDescription>Sign out of every browser and phone, including this one.</CardDescription></CardHeader>
        <CardContent>
          <Button
            variant="outline"
            disabled={signingOut}
            onClick={async () => {
              setSigningOut(true)
              try {
                await signOutEverywhere()
                await endSession('Signed out everywhere.')
              } catch (e) {
                toast.error(parseApiError(e).detail || 'Could not sign out')
                setSigningOut(false)
              }
            }}
          >
            Sign out everywhere
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}
